import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Box, MenuItem, TextField } from '@mui/material';

const OPTIONS = [
  { label: 'Today',         value: 'today' },
  { label: 'Yesterday',     value: 'yesterday' },
  { label: 'This Week',     value: 'this_week' },
  { label: 'Last Week',     value: 'last_week' },
  { label: 'This Month',    value: 'this_month' },
  { label: 'Last Month',    value: 'last_month' },
  { label: 'Last 3 Months', value: 'last_3_months' },
  { label: 'Custom',        value: 'custom' },
];

/**
 * Builds the API payload:
 *   { created_on: 'this_month' }
 *   { created_on: 'custom', start_date: 'YYYY-MM-DD', end_date: 'YYYY-MM-DD' }
 *   null → nothing selected
 */
function buildValue(selected, startDate, endDate) {
  if (!selected) return null;
  if (selected === 'custom') {
    return {
      created_on: 'custom',
      ...(startDate && { start_date: startDate.format('YYYY-MM-DD') }),
      ...(endDate && { end_date: endDate.format('YYYY-MM-DD') }),
    };
  }
  return { created_on: selected };
}

/**
 * value shape (controlled from parent):
 *   null
 *   { created_on: 'this_month' }
 *   { created_on: 'custom', start_date: 'YYYY-MM-DD', end_date: 'YYYY-MM-DD' }
 */
export default function DatePickerCustom({ value, onChange }) {
  const selected = value?.created_on ?? '';
  const startDate = value?.start_date ? dayjs(value.start_date) : null;
  const endDate = value?.end_date ? dayjs(value.end_date) : null;

  const isCustom = selected === 'custom';

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val !== 'custom') {
      onChange?.(buildValue(val, null, null));
    } else {
      onChange?.(buildValue(val, null, null));
    }
  };

  const handleStartChange = (date) => {
    const newEnd = endDate && date && endDate.isBefore(date, 'day') ? null : endDate;
    onChange?.(buildValue(selected, date, newEnd));
  };

  const handleEndChange = (date) => {
    onChange?.(buildValue(selected, startDate, date));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {/* Select */}
        <TextField
          select
          label="Date"
          size="small"
          value={selected}
          onChange={handleSelectChange}
          sx={{ minWidth: 160 }}
        >
          {OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Custom date pickers — only shown when Custom is selected */}
        {isCustom && (
          <>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={handleStartChange}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={handleEndChange}
              minDate={startDate || undefined}
              slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
}